import type { PlayerColor } from '@/shared/board/types'
import type { Move, MoveKind, TokenPosition } from '@/shared/game/types'
import { startIndex } from './board'
import {
  type GameState,
  isShielded,
  occupant,
  ringOccupant,
  type TokenRef,
  tokensOf,
} from './state'
import { jump as cornerJump, path, ring } from './track'

/**
 * Ходы на один кубик — порт App\Domain\Ludo\MoveGenerator.
 *
 * Здесь живут правила прохода и рубки: из двора выходят по шестёрке, через
 * фишки не перепрыгивают, на клетке стоит не больше одной, в дом входят
 * точным числом, из угла прыгают на 1–3 угла вперёд.
 *
 * Правило «ходить на максимум» сюда не входит: оно про выбор между ходами,
 * а не про их допустимость, и живёт в resolver.ts.
 */

/** Максимальный прыжок: углов всего четыре, на четвёртый фишка вернулась бы к себе. */
const MAX_JUMP = 3

export function movesForDie(state: GameState, player: PlayerColor, die: number): Move[] {
  const moves: Move[] = []

  tokensOf(state, player).forEach((position, index) => {
    const token: TokenRef = { color: player, index }

    for (const move of [
      exitMove(state, token, position, die),
      advanceMove(state, token, position, die),
      jumpMove(state, token, position, die),
    ]) {
      if (move !== null) moves.push(move)
    }
  })

  return moves
}

/** Выход из двора: только по шестёрке и только на свою стартовую клетку. */
function exitMove(
  state: GameState,
  token: TokenRef,
  position: TokenPosition,
  die: number,
): Move | null {
  if (die !== 6 || position.location !== 'base') return null

  const target = ring(startIndex(token.color))
  const standing = ringOccupant(state, target.index)

  if (standing?.color === token.color) return null
  // Фишку под щитом не рубят ничем, и выходом на старт тоже.
  if (standing !== null && isShielded(state, standing)) return null

  return move(token, die, 'exit', position, [target], standing)
}

/** Обычный ход по кольцу и дому. */
function advanceMove(
  state: GameState,
  token: TokenRef,
  position: TokenPosition,
  die: number,
): Move | null {
  const cells = path(token.color, position, die)

  if (cells === null) return null

  return walk(state, token, position, die, 'advance', cells)
}

/** Прыжок из угла в угол. */
function jumpMove(
  state: GameState,
  token: TokenRef,
  position: TokenPosition,
  die: number,
): Move | null {
  if (die < 1 || die > MAX_JUMP) return null

  const corners = cornerJump(token.color, position, die)

  if (corners === null) return null

  return walk(
    state,
    token,
    position,
    die,
    'jump',
    corners.map((index) => ring(index)),
  )
}

/**
 * Проход по готовому пути: промежуточные клетки должны быть пусты, на
 * конечной чужая фишка рубится, а своя ход отменяет.
 */
function walk(
  state: GameState,
  token: TokenRef,
  from: TokenPosition,
  die: number,
  kind: MoveKind,
  cells: TokenPosition[],
): Move | null {
  const target = cells[cells.length - 1]

  for (const step of cells.slice(0, -1)) {
    if (occupant(state, token.color, step) !== null) return null
  }

  const standing = occupant(state, token.color, target)

  if (standing?.color === token.color) return null
  if (standing !== null && isShielded(state, standing)) return null

  return move(token, die, kind, from, cells, standing)
}

function move(
  token: TokenRef,
  die: number,
  kind: MoveKind,
  from: TokenPosition,
  cells: TokenPosition[],
  captures: TokenRef | null,
): Move {
  return {
    color: token.color,
    token: token.index,
    die,
    kind,
    from,
    to: cells[cells.length - 1],
    path: cells,
    captures: captures === null ? null : { color: captures.color, token: captures.index },
  }
}
